import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ClipboardList,
  FileText,
  MessageSquare,
  Play,
  Power,
  RefreshCw,
  Stethoscope,
  UserRound,
} from "lucide-react";
import {
  buildModule1IdempotencyKey,
  createModule1Session,
  endModule1Session,
  getModule1ActiveSession,
  getModule1History,
  getModule1Options,
  initModule1Session,
  normalizeApiError,
} from "../services/module1Sessions";

const DEFAULT_PATIENT_TYPES = [{ id: "typical", label: "Typical / cooperative" }];

export default function DoctorSimulator() {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState(["Random"]);
  const [selectedSpecialty, setSelectedSpecialty] = useState("Random");
  const [patientTypes, setPatientTypes] = useState(DEFAULT_PATIENT_TYPES);
  const [selectedPatientType, setSelectedPatientType] = useState("typical");
  const [activeSession, setActiveSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [confirmEndSession, setConfirmEndSession] = useState(null);
  const startActionRef = useRef(null);
  const endActionRef = useRef(null);

  const loadOptions = useCallback(async () => {
    try {
      const data = await getModule1Options();
      const specialtyList = data?.options?.specialties || ["Random"];
      setSpecialties(specialtyList);
      setSelectedSpecialty((current) =>
        specialtyList.includes(current)
          ? current
          : specialtyList.includes("Random")
          ? "Random"
          : specialtyList[0]
      );

      const loadedPatientTypes = data?.options?.patient_types || [];
      if (Array.isArray(loadedPatientTypes) && loadedPatientTypes.length > 0) {
        setPatientTypes(loadedPatientTypes);
        setSelectedPatientType((current) =>
          loadedPatientTypes.some((item) => item?.id === current)
            ? current
            : loadedPatientTypes.some((item) => item?.id === "typical")
            ? "typical"
            : String(loadedPatientTypes[0]?.id || "typical")
        );
      }
    } catch (err) {
      console.error("Failed to load Module 1 options:", err);
    }
  }, []);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRecovery(null);
    try {
      const active = await getModule1ActiveSession();
      setActiveSession(active?.active_session || null);

      const historyResponse = await getModule1History(50);
      setHistory(Array.isArray(historyResponse?.sessions) ? historyResponse.sessions : []);
    } catch (err) {
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadWorkspace();
  }, [loadOptions, loadWorkspace]);

  const handleStartCase = async () => {
    const initPayload = {
      specialty: selectedSpecialty,
      patient_type: selectedPatientType,
    };
    let action = startActionRef.current;
    if (action?.inFlight) return;
    if (!isSameStartAction(action, initPayload)) {
      action = {
        key: buildModule1IdempotencyKey(),
        payload: initPayload,
        sessionId: null,
        inFlight: false,
        retryable: false,
      };
      startActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;
    setStarting(true);
    setError(null);
    setRecovery(null);
    try {
      if (!action.sessionId) {
        const created = await createModule1Session();
        action.sessionId = created?.session_id;
      }
      await initModule1Session(action.sessionId, action.payload, {
        idempotencyKey: action.key,
      });
      startActionRef.current = null;
      navigate(`/doctor/session/${action.sessionId}`);
    } catch (err) {
      const apiError = normalizeApiError(err);
      if (apiError.errorCode === "module1_active_session_conflict") {
        startActionRef.current = null;
        await recoverFromActiveConflict(apiError);
      } else {
        if (isAmbiguousNetworkError(apiError) && action.sessionId) {
          action.retryable = true;
        } else if (startActionRef.current === action) {
          startActionRef.current = null;
        }
        setError(apiError);
      }
    } finally {
      if (startActionRef.current === action) {
        action.inFlight = false;
      }
      setStarting(false);
    }
  };

  const recoverFromActiveConflict = async (apiError) => {
    try {
      const active = await getModule1ActiveSession();
      const existing = active?.active_session || null;
      setActiveSession(existing);
      if (existing) {
        setRecovery({
          message: "You already have an active case. Continue it instead.",
          session: existing,
          correlationId: apiError.correlationId,
        });
        return;
      }
      await loadWorkspace();
    } catch (err) {
      setError(normalizeApiError(err));
    }
  };

  const handleEndActiveSession = async () => {
    if (!confirmEndSession) return;
    const sessionId = confirmEndSession.session_id;
    let action = endActionRef.current;
    if (action?.inFlight) return;
    if (!isSameSessionAction(action, sessionId)) {
      action = {
        key: buildModule1IdempotencyKey(),
        sessionId,
        payload: {},
        inFlight: false,
        retryable: false,
      };
      endActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;
    setEnding(true);
    setError(null);
    try {
      await endModule1Session(sessionId, { idempotencyKey: action.key });
      endActionRef.current = null;
      setConfirmEndSession(null);
      await loadWorkspace();
    } catch (err) {
      const apiError = normalizeApiError(err);
      if (isAmbiguousNetworkError(apiError)) {
        action.retryable = true;
      } else if (endActionRef.current === action) {
        endActionRef.current = null;
      }
      setError(apiError);
    } finally {
      if (endActionRef.current === action) {
        action.inFlight = false;
      }
      setEnding(false);
    }
  };

  return (
    <div className="doctor-sim ds-workspace">
      <PageHeader />

      {loading ? (
        <WorkspaceLoading />
      ) : (
        <>
          {error && <ErrorNotice error={error} onRetry={loadWorkspace} />}
          {recovery && <ConflictRecovery recovery={recovery} />}

          <section className="ds-primary-workspace-card">
            {activeSession ? (
              <ActiveSessionCard
                session={activeSession}
                ending={ending}
                onContinue={() => navigate(`/doctor/session/${activeSession.session_id}`)}
                onEnd={() => setConfirmEndSession(activeSession)}
              />
            ) : (
              <StartCaseCard
                specialties={specialties}
                selectedSpecialty={selectedSpecialty}
                onSpecialtyChange={setSelectedSpecialty}
                patientTypes={patientTypes}
                selectedPatientType={selectedPatientType}
                onPatientTypeChange={setSelectedPatientType}
                starting={starting}
                onStart={handleStartCase}
              />
            )}
          </section>

          <HistoryList sessions={history} onOpen={(id) => navigate(`/doctor/session/${id}`)} />
        </>
      )}

      {confirmEndSession && (
        <ConfirmEndDialog
          session={confirmEndSession}
          ending={ending}
          onCancel={() => setConfirmEndSession(null)}
          onConfirm={handleEndActiveSession}
        />
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div className="ds-header">
      <h1>Doctor Simulator</h1>
      <p className="ds-subtitle">
        You are the Doctor. The AI is the patient. Choose a medical specialty and
        patient persona, then diagnose and treat the patient to complete the
        simulation.
      </p>
    </div>
  );
}

function WorkspaceLoading() {
  return (
    <div className="ds-workspace-loading">
      <RefreshCw size={18} />
      Loading Module 1 workspace...
    </div>
  );
}

function StartCaseCard({
  specialties,
  selectedSpecialty,
  onSpecialtyChange,
  patientTypes,
  selectedPatientType,
  onPatientTypeChange,
  starting,
  onStart,
}) {
  return (
    <div className="ds-setup-panel ds-workspace-panel">
      <div className="ds-setup-title">
        <ClipboardList size={16} />
        Case Configuration
      </div>

      <div className="ds-setup-fields">
        <div className="ds-field-group">
          <label htmlFor="ds-specialty">Medical Specialty</label>
          <select
            id="ds-specialty"
            value={selectedSpecialty}
            onChange={(event) => onSpecialtyChange(event.target.value)}
            disabled={starting}
            className="ds-select"
          >
            {specialties.map((specialty) => (
              <option key={specialty} value={specialty}>
                {specialty}
              </option>
            ))}
          </select>
        </div>

        <div className="ds-field-group">
          <label htmlFor="ds-patient-type">Patient Persona</label>
          <select
            id="ds-patient-type"
            value={selectedPatientType}
            onChange={(event) => onPatientTypeChange(event.target.value)}
            disabled={starting}
            className="ds-select"
          >
            {patientTypes.map((patientType) => (
              <option key={patientType.id} value={patientType.id}>
                {patientType.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ds-setup-actions">
        <button
          className="btn btn-primary ds-start-btn"
          onClick={onStart}
          disabled={starting}
        >
          <Play size={14} />
          {starting ? "Starting case..." : "Start Case"}
        </button>
      </div>
    </div>
  );
}

function ActiveSessionCard({ session, ending, onContinue, onEnd }) {
  return (
    <div className="ds-active-card">
      <div className="ds-active-card-main">
        <div className="ds-status-row">
          <StatusBadge status={session.display_status || session.state} />
          <span className="ds-muted-inline">
            Last activity {formatDateTime(session.last_activity_at)}
          </span>
        </div>
        <h2>{session.case_title || "Active Module 1 case"}</h2>
        <div className="ds-active-meta">
          <MetaPill icon={<Stethoscope size={13} />} label={session.specialty || "Unknown"} />
          <MetaPill icon={<UserRound size={13} />} label={session.patient_type || "Unknown"} />
          {session.patient_name && (
            <MetaPill icon={<UserRound size={13} />} label={session.patient_name} />
          )}
          <MetaPill
            icon={<MessageSquare size={13} />}
            label={`${session.message_count || 0} messages`}
          />
        </div>
        {session.last_message_preview && (
          <p className="ds-preview-text">{session.last_message_preview}</p>
        )}
      </div>
      <div className="ds-active-actions">
        <button className="btn btn-primary" onClick={onContinue}>
          <Play size={14} />
          Continue Case
        </button>
        <button className="btn btn-secondary" onClick={onEnd} disabled={ending}>
          <Power size={14} />
          {ending ? "Ending..." : "End Case and Generate Assessment"}
        </button>
      </div>
    </div>
  );
}

function HistoryList({ sessions, onOpen }) {
  return (
    <section className="ds-history-section" aria-label="Module 1 session history">
      <div className="ds-history-header">
        <div>
          <h2>Previous Cases</h2>
          <p>Review active, completed, abandoned, and error sessions.</p>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="ds-history-empty">
          <FileText size={18} />
          No previous cases yet.
        </div>
      ) : (
        <div className="ds-history-list">
          {sessions.map((session) => (
            <button
              key={session.session_id}
              type="button"
              className="ds-history-item"
              onClick={() => onOpen(session.session_id)}
            >
              <div className="ds-history-item-main">
                <div className="ds-history-item-title-row">
                  <span className="ds-history-title">
                    {session.case_title || "Module 1 case"}
                  </span>
                  <StatusBadge status={session.display_status || session.state} />
                </div>
                <div className="ds-history-meta">
                  <span>{session.specialty || "Unknown specialty"}</span>
                  <span>{session.patient_type || "Unknown persona"}</span>
                  <span>{formatDateTime(session.last_activity_at || session.created_at)}</span>
                </div>
                <div className="ds-history-footnote">
                  {session.last_message_preview || "No recent message preview available."}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function ConflictRecovery({ recovery }) {
  const navigate = useNavigate();
  return (
    <div className="ds-recovery-card">
      <AlertCircle size={16} />
      <div>
        <strong>{recovery.message}</strong>
        {recovery.correlationId && (
          <p className="ds-correlation">Reference: {recovery.correlationId}</p>
        )}
      </div>
      <button
        className="btn btn-primary"
        onClick={() => navigate(`/doctor/session/${recovery.session.session_id}`)}
      >
        Continue Case
      </button>
    </div>
  );
}

function ErrorNotice({ error, onRetry }) {
  return (
    <div className="ds-setup-error ds-workspace-error" role="alert">
      <AlertCircle size={14} />
      <div>
        <strong>{error.message}</strong>
        {error.correlationId && <p>Reference: {error.correlationId}</p>}
      </div>
      <button className="btn btn-secondary" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function ConfirmEndDialog({ session, ending, onCancel, onConfirm }) {
  return (
    <div className="ds-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ds-confirm-dialog">
        <h2>End Case and Generate Assessment?</h2>
        <p>
          This will finish the current case and generate an evaluator assessment.
          You will not be able to continue editing this case after it is completed.
        </p>
        <p className="ds-confirm-session">{session.case_title || session.session_id}</p>
        <div className="ds-confirm-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={ending}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={ending}>
            {ending ? "Ending..." : "End Case and Generate Assessment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MetaPill({ icon, label }) {
  return (
    <span className="ds-context-badge ds-context-badge-secondary">
      {icon}
      {label}
    </span>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || "unknown").toLowerCase();
  const labels = {
    active: "Active",
    running: "Active",
    completed: "Completed",
    abandoned: "Abandoned",
    error: "Error",
  };
  return (
    <span className={`ds-state-indicator ds-state-${normalized}`}>
      {labels[normalized] || "Unknown"}
    </span>
  );
}

function formatDateTime(value) {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function isSameStartAction(action, payload) {
  return (
    action?.retryable &&
    action.payload?.specialty === payload.specialty &&
    action.payload?.patient_type === payload.patient_type
  );
}

function isSameSessionAction(action, sessionId) {
  return action?.retryable && action.sessionId === sessionId;
}

function isAmbiguousNetworkError(error) {
  return error?.errorCode === "network_error" && !error?.status;
}
